/**
 * Management Remote 頁面配置
 *
 * 定義所有 management 模組的頁面，自動生成：
 * - vite.config exposes
 * - remoteModules
 * - pagePermissions
 * - sideNavMenus
 */

import type { RemoteModuleConfig, PageConfig } from './types';

// ============================================================================
// 頁面配置
// ============================================================================

export const pages: PageConfig[] = [
    // ===== 設計系統 =====
    {
        name: 'DesignSystemDemo',
        path: './views/DesignSystemDemo.vue',
        title: '設計系統展示',
        mode: 'test',
        default: false,
    },

    // ===== 系統設定 =====
    {
        name: 'AlarmSettings',
        path: './views/AlarmSettings.vue',
        title: '告警設定',
        mode: 'management',
        icon: 'icofont-gear',
        default: false,
        showInSideNav: true,
        standalone: { show: true, group: '系統設定', order: 1 },
    },
    {
        name: 'TaipowerPriceSettings',
        path: './views/TaipowerPriceSettings.vue',
        title: '電價時段',
        mode: 'ess',
        icon: 'icofont-clock-time',
        default: false,
        showInSideNav: true,
        standalone: { show: true, group: '儲能', order: 1 },
    },
    {
        name: 'EssStrategySettings',
        path: './views/EssStrategySettings.vue',
        title: '策略設定',
        mode: 'ess',
        icon: 'icofont-cube',
        default: false,
        showInSideNav: true,
        standalone: { show: true, group: '儲能', order: 2 },
    },

    // ===== 組織管理 =====
    {
        name: 'CreateOrganization',
        path: './views/CreateOrganization.vue',
        title: '新增組織',
        mode: 'management',
        icon: 'icofont-people',
        default: false,
        showInSideNav: true,
        standalone: { show: true, group: '組織', order: 1 },
    },
    {
        name: 'OrganizationList',
        path: './views/OrganizationList.vue',
        title: '組織列表',
        mode: 'management',
        icon: 'icofont-company',
        default: false,
        showInSideNav: true,
        standalone: { show: true, group: '組織', order: 2 },
    },

    // ===== 資料庫工具 =====
    {
        name: 'MySQLTest',
        path: './views/MySQLTest.vue',
        title: 'MySQL 測試',
        mode: 'test',
        icon: 'icofont-database-add',
        default: false,
        showInSideNav: true,
        standalone: { show: true, group: '資料庫', order: 1 },
    },
    {
        name: 'TDengineSchemaGenerator',
        path: './views/TDengineSchemaGenerator.vue',
        title: 'TDEngine 產生器',
        mode: 'management',
        icon: 'icofont-database-locked',
        default: false,
        showInSideNav: true,
        standalone: { show: true, group: '資料庫', order: 2 },
    },
    {
        name: 'ReplaceUserId',
        path: './views/ReplaceUserId.vue',
        title: '用戶遷移',
        mode: 'management',
        icon: 'icofont-exchange',
        default: false,
        showInSideNav: true,
        standalone: { show: true, group: '資料庫', order: 3 },
    },
    {
        name: 'EscCommandHistory',
        path: './views/EscCommandHistory.vue',
        title: '指令歷程',
        mode: 'management',
        icon: 'icofont-listine-dots',
        default: false,
        showInSideNav: true,
        standalone: { show: true, group: '資料庫', order: 4 },
    },

    // ===== 監控工具 =====
    {
        name: 'CronMonitor',
        path: './views/CronMonitor.vue',
        title: '排程監控',
        mode: 'management',
        icon: 'icofont-wall-clock',
        default: false,
        showInSideNav: true,
        standalone: { show: true, group: '監控', order: 1 },
    },
    {
        name: 'FindEquipment',
        path: './views/FindEquipment.vue',
        title: '設備查詢',
        mode: 'management',
        icon: 'icofont-search-1',
        default: false,
        showInSideNav: true,
        standalone: { show: true, group: '監控', order: 2 },
    },
    {
        name: 'MQTTCommand',
        path: './views/MQTTCommand.vue',
        title: 'MQTT 命令',
        mode: 'management',
        icon: 'icofont-send-mail',
        default: false,
        showInSideNav: true,
        standalone: { show: true, group: '監控', order: 3 },
    },
    {
        name: 'MqttBridgeStatus',
        path: './views/MqttBridgeStatus.vue',
        title: 'MQTT Bridge (舊)',
        mode: 'management',
        default: false,
        standalone: { show: true, group: '監控', order: 4 },
    },
    {
        name: 'MqttServiceStatus',
        path: './views/MqttServiceStatus.vue',
        title: 'MQTT Service',
        mode: 'management',
        icon: 'icofont-pulse',
        default: false,
        showInSideNav: true,
        standalone: { show: true, group: '監控', order: 5 },
    },

    // ===== 權限管理（已遷移至 core/src/views/admin/） =====
    // PagePermissionByPage 和 PagePermissionByOrg 現在是 core 本地頁面
    // 權限 key 為 admin:PagePermissionByOrg、admin:PagePermissionByPage
    {
        name: 'PermissionTest',
        path: './views/PermissionTest.vue',
        title: '權限測試',
        mode: 'management',
        icon: 'icofont-test-tube-alt',
        default: false,
        showInSideNav: true,
        standalone: { show: true, group: '權限', order: 3 },
    },

    // ===== 測試工具 =====
    {
        name: 'UniverPoc',
        path: './views/UniverPoc.vue',
        title: 'POC 測試',
        mode: 'test',
        icon: 'icofont-table',
        default: false,
        showInSideNav: true,
        standalone: { show: true, group: '測試', order: 1 },
    },
    {
        name: 'UniverFin',
        path: './views/UniverFin.vue',
        title: 'FIN 測試',
        mode: 'test',
        icon: 'icofont-file-excel',
        default: false,
        showInSideNav: true,
        standalone: { show: true, group: '測試', order: 2 },
    },
];

// ============================================================================
// 模組完整配置
// ============================================================================

export const config: RemoteModuleConfig = {
    name: 'management',
    federationName: 'management_remote',
    port: 7006,
    pages,
};

export default config;
